import React, { useEffect, useState } from "react";
import { InputGroup } from "reactstrap";
import { Can } from "config/Can";
import SearchableSelect from "components/common/SearchableSelect/SearchableSelect";
import LoaderComponent from "components/common/Loader/Loader";
import ButtonComponent from "components/common/Button/ButtonComponent";
import Toaster from "components/common/Toaster/Toaster";
import RearrangeButton from "./RearrangeButton/RearrangeButton";
import BannerCardComponent from "./BannerCardComponent/BannerCardComponent";
import BannerSortComponent from "./BannerSort/BannerSortComponent";

import { IBannersResponseNormalized } from "interfaces/banner.interface";
import { IIdValuePairResponse, ISelectDropdownOption } from "interfaces/Index";
import { IBannerListingPage } from "./BannerListingPage.interface";

import "./BannerListingPage.scss";
import { isEmpty } from "lodash";
import { removeCharacterFromStringAndCapitalize } from "utils/lib";

const BannerListingPage: React.FunctionComponent<IBannerListingPage> = (
  props: IBannerListingPage
) => {
  const {
    retainedSelectedCity,
    isLoading,
    cityList,
    fetchCities,
    getBanners,
    error,
    isError,
    banner,
    isBannerFetched,
    updateBannerSequence,
    isBannerSequenceUpdated,
    editPageHandler,
    isBannerUpdated,
    fetchCustomerChannelAction,
    customerConfig,
    selectedCustomerChannel,
  } = props;

  const [isArrange, setArrange] = useState(false);
  const [bannerState, setBannerState] = useState<IBannersResponseNormalized[]>(
    []
  );
  const [selectedCity, setSelectedCity] = useState<IIdValuePairResponse | null>(
    retainedSelectedCity
  );
  const [selectedCustomerConfig, setSelectedCustomerConfig] =
    useState<IIdValuePairResponse>({} as IIdValuePairResponse);
  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });
  const [customerChannel, setCustomerChannel] = useState<
    IIdValuePairResponse[]
  >([]);

  useEffect(() => {
    if (!cityList.length) fetchCities();
    if (isEmpty(customerConfig)) fetchCustomerChannelAction();
  }, []);

  useEffect(() => {
    if (!selectedCity && cityList.length > 0) {
      const newSelectedCity = cityList.length ? cityList[0] : null;
      handleSelectedCityChange(newSelectedCity);
    }
  }, [cityList]);

  useEffect(() => {
    if (banner) {
      setBannerState(banner.slice());
    }
  }, [isBannerFetched]);

  useEffect(() => {
    if (isBannerUpdated) {
      handleToaster(false, "Banner successfully updated");
      refreshList(
        selectedCity as IIdValuePairResponse,
        selectedCustomerConfig as IIdValuePairResponse
      );
    }
  }, [isBannerUpdated]);

  useEffect(() => {
    if (isBannerSequenceUpdated) {
      handleToaster(false, "Banner sequence updated successfully");
      setArrange(false);
      refreshList(
        selectedCity as IIdValuePairResponse,
        selectedCustomerConfig as IIdValuePairResponse
      );
    }
  }, [isBannerSequenceUpdated]);

  useEffect(() => {
    if (isError && error) {
      alert((error! as any).errors);
    }
  }, [isError]);

  useEffect(() => {
    if (isEmpty(customerChannel)) {
      let newMapping = customerConfig?.customerChannels?.map((item) => {
        return {
          id: item.id,
          status: item.id,
          name: removeCharacterFromStringAndCapitalize(item.title, "_"),
          label: removeCharacterFromStringAndCapitalize(item.title, "_"),
          title: removeCharacterFromStringAndCapitalize(item.title, "_"),
          value: item.value,
        };
      });
      if (!isEmpty(selectedCustomerChannel)) {
        handleCustomerChannelChange(selectedCustomerChannel);
      } else {
        const newSelectedCustomerChannel = newMapping?.length
          ? newMapping[0]
          : null;
        handleCustomerChannelChange(newSelectedCustomerChannel);
      }
      setCustomerChannel(newMapping as IIdValuePairResponse[]);
    }
  }, [customerConfig]);
  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };

  const refreshList = (
    selectedCity: IIdValuePairResponse,
    customerChannel: IIdValuePairResponse
  ) => {
    getBanners(
      selectedCity.id,
      customerChannel.id
        ? customerChannel.id
        : selectedCustomerChannel?.id || ""
    );
  };

  const handleSelectedCityChange = (
    selectedCity: IIdValuePairResponse | null
  ) => {
    if (selectedCity) {
      setSelectedCity(selectedCity);
      if (!isEmpty(cityList) && !isEmpty(customerConfig?.customerChannels)) {
        refreshList(
          selectedCity,
          selectedCustomerConfig as IIdValuePairResponse
        );
      }
    }
  };

  const toggleRearrange = () => {
    setArrange(!isArrange);
  };

  const handleRearrangeCancel = () => {
    toggleRearrange();
    setBannerState(banner.slice());
  };

  const handleBannerClick = (bannerIndex: number) => {
    editPageHandler(
      bannerState[bannerIndex],
      selectedCity as IIdValuePairResponse,
      selectedCustomerConfig as IIdValuePairResponse
    );
  };

  const handleBannerSortEnd = (sortedBanners: IBannersResponseNormalized[]) => {
    setBannerState(sortedBanners);
  };

  const handleCustomerChannelChange = (
    selectedCustomerConfig: IIdValuePairResponse | null
  ) => {
    if (!isEmpty(selectedCustomerConfig)) {
      setSelectedCustomerConfig(selectedCustomerConfig as IIdValuePairResponse);
      if (!isEmpty(cityList) && !isEmpty(customerConfig?.customerChannels)) {
        refreshList(
          selectedCity as IIdValuePairResponse,
          selectedCustomerConfig as IIdValuePairResponse
        );
      }
    }
  };
  const confirmBannerSortHandler = () => {
    const bannerIds = bannerState.map((banner) => banner.id);
    updateBannerSequence(selectedCity?.id as string, bannerIds);
  };

  const renderActionButtons = () => {
    return (
      <div className="action-buttons">
        <ButtonComponent
          buttonText="cancel"
          buttonClass="btn-primary-outline"
          onClickHandler={handleRearrangeCancel}
        />
        <ButtonComponent
          buttonText="Confirm"
          buttonClass="btn btn-primary confirm-button"
          onClickHandler={confirmBannerSortHandler}
        />
      </div>
    );
  };

  const renderBanners = () => {
    return (bannerState || []).map((banner, index) => {
      return (
        <BannerCardComponent
          key={index}
          banner={banner}
          clickHandler={() => handleBannerClick(index)}
        />
      );
    });
  };

  return (
    <div className="banner-list-container">
      {isLoading && <LoaderComponent />}
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
        toasterDuration={6000}
      >
        {showToaster.message || ""}
      </Toaster>
      <section className="relative container sticky-top">
        <section className="row">
          <section className="col-6">
            <InputGroup>
              <section className="row">
                <section className="col-6">
                  <div className="input-group-append">
                    <SearchableSelect
                      id="selectedCity"
                      isSearchable={true}
                      options={cityList}
                      selectedValue={selectedCity}
                      onChange={handleSelectedCityChange}
                    />
                  </div>
                </section>
                <section className="col-6">
                  <div className="input-group-append">
                    <SearchableSelect
                      id="selectedCustomerChannel"
                      isSearchable={true}
                      isDisabled={isEmpty(selectedCity)}
                      options={customerChannel}
                      selectedValue={selectedCustomerConfig}
                      onChange={handleCustomerChannelChange}
                    />
                  </div>
                </section>
              </section>
            </InputGroup>
          </section>
          <section className="col-6">
            <InputGroup>
              <div className="banner-action-button-container">
                {bannerState.length > 0 &&
                  (isArrange ? (
                    renderActionButtons()
                  ) : (
                    <RearrangeButton clickHandler={toggleRearrange} />
                  ))}
              </div>
            </InputGroup>
          </section>
        </section>
      </section>
      <Can I="read" a="banner">
        {bannerState.length > 0 ? (
          <div className="banner-list">
            {isArrange ? (
              <BannerSortComponent
                banners={bannerState}
                handleSortEnd={handleBannerSortEnd}
              />
            ) : (
              renderBanners()
            )}
          </div>
        ) : (
          <h4 style={{ marginTop: "30px" }}>No Banners Found</h4>
        )}
      </Can>
    </div>
  );
};

export default BannerListingPage;
